import { Link } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, Gavel, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { AdminAuction, AdminAuctionSale } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { toast } from '@nucleus-commerce/ui/components/sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { displayTimeZone, useStore } from '@/shared/store/store-context';

import { useRemoveSaleLot, useReorderSaleLots } from '../hooks/use-sale-mutations';
import { useSaleLots } from '../hooks/use-sales';
import { canEditLots } from '../schemas/sale-rules';

import { AddSaleLotDialog } from './add-sale-lot-dialog';
import { AuctionFormDialog } from './auction-form-dialog';
import { AuctionOutcomeBadge, AuctionStatusBadge } from './auction-status-badge';
import { AuctionWhen } from './auction-when';
import { LotThumb } from './lot-thumb';

function Price({ auction }: { auction: AdminAuction }) {
  const { t } = useTranslation('auctions');
  const format = useFormat();
  const price = auction.hammerPrice ?? auction.currentPrice ?? auction.startingPrice;
  return (
    <span className="flex flex-col items-end tabular-nums">
      <span className="font-medium">{format.money(price)}</span>
      <span className="text-muted-foreground text-xs">
        {auction.hammerPrice
          ? t('price.hammer')
          : auction.currentPrice
            ? t('price.current')
            : t('price.starting')}
      </span>
    </span>
  );
}

/**
 * The sale's lots in lot order. While the sale is a draft, lots join (an existing draft auction,
 * or a new one made in place), move up and down and leave; once scheduled the list is read-only
 * and every row links to its auction.
 */
export function SaleLotsCard({
  storeId,
  sale,
  canWrite,
}: {
  storeId: string;
  sale: AdminAuctionSale;
  canWrite: boolean;
}) {
  const { t } = useTranslation('auctions');
  const store = useStore();
  const message = useErrorMessage();
  const lots = useSaleLots(storeId, sale.id);
  const reorder = useReorderSaleLots(storeId, sale.id);
  const remove = useRemoveSaleLot(storeId, sale.id);
  const [dialog, setDialog] = useState<'add' | 'new' | null>(null);
  const editable = canWrite && canEditLots(sale);
  const rows = lots.data ?? [];

  const move = (index: number, delta: number) => {
    const ids = rows.map((row) => row.id);
    const target = index + delta;
    const [moved] = ids.splice(index, 1);
    if (moved === undefined) return;
    ids.splice(target, 0, moved);
    reorder.mutate(ids, { onError: (error) => toast.error(message(error)) });
  };

  return (
    <Card data-tour="auctions.sale.lots">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <CardTitle>{t('sales.lots.title', { count: sale.lotCount })}</CardTitle>
        {editable ? (
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setDialog('add')}>
              {t('sales.lots.addExisting')}
            </Button>
            <Button type="button" size="sm" onClick={() => setDialog('new')}>
              <Plus aria-hidden />
              {t('sales.lots.new')}
            </Button>
          </div>
        ) : null}
      </CardHeader>
      <CardContent>
        {lots.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : lots.error ? (
          <ErrorState error={lots.error} onRetry={() => void lots.refetch()} />
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground flex items-center gap-2 text-sm">
            <Gavel aria-hidden className="size-4" />
            {editable ? t('sales.lots.emptyEditable') : t('sales.lots.empty')}
          </p>
        ) : (
          <Table aria-label={t('sales.lots.tableLabel')}>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">{t('sales.lots.columns.number')}</TableHead>
                <TableHead>{t('columns.lot')}</TableHead>
                <TableHead>{t('columns.status')}</TableHead>
                <TableHead className="text-right">{t('columns.price')}</TableHead>
                <TableHead className="text-right">{t('columns.bids')}</TableHead>
                <TableHead>{t('columns.when')}</TableHead>
                {editable ? (
                  <TableHead className="text-right">
                    <span className="sr-only">{t('sales.lots.columns.actions')}</span>
                  </TableHead>
                ) : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((auction, index) => (
                <TableRow key={auction.id}>
                  <TableCell className="tabular-nums">
                    {auction.sale?.lotNumber ?? index + 1}
                  </TableCell>
                  <TableCell>
                    <span className="flex min-w-0 items-center gap-3">
                      <LotThumb lot={auction.lot} />
                      <span className="flex min-w-0 flex-col">
                        <Link
                          to="/s/$storeId/auctions/$auctionId"
                          params={{ storeId, auctionId: auction.id }}
                          className="truncate font-medium hover:underline"
                        >
                          {auction.lot.title}
                        </Link>
                        <span className="text-muted-foreground truncate text-xs">
                          {[auction.lot.variantTitle, auction.lot.sku]
                            .filter(Boolean)
                            .join(' · ') || t('lot.noVariant')}
                        </span>
                      </span>
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="flex flex-wrap gap-1">
                      <AuctionStatusBadge status={auction.status} />
                      {auction.outcome ? <AuctionOutcomeBadge outcome={auction.outcome} /> : null}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Price auction={auction} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{auction.bidCount}</TableCell>
                  <TableCell>
                    <AuctionWhen auction={auction} />
                  </TableCell>
                  {editable ? (
                    <TableCell>
                      <span className="flex justify-end gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          disabled={index === 0 || reorder.isPending}
                          onClick={() => move(index, -1)}
                          aria-label={t('sales.lots.moveUp', { title: auction.lot.title })}
                        >
                          <ArrowUp aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          disabled={index === rows.length - 1 || reorder.isPending}
                          onClick={() => move(index, 1)}
                          aria-label={t('sales.lots.moveDown', { title: auction.lot.title })}
                        >
                          <ArrowDown aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="text-destructive hover:text-destructive"
                          disabled={remove.isPending}
                          onClick={() =>
                            remove.mutate(auction.id, {
                              onError: (error) => toast.error(message(error)),
                            })
                          }
                          aria-label={t('sales.lots.remove', { title: auction.lot.title })}
                        >
                          <X aria-hidden />
                        </Button>
                      </span>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      {editable ? (
        <>
          <AddSaleLotDialog
            storeId={storeId}
            saleId={sale.id}
            open={dialog === 'add'}
            onOpenChange={(open) => {
              if (!open) setDialog(null);
            }}
          />
          <AuctionFormDialog
            storeId={storeId}
            currency={sale.currency}
            timeZone={displayTimeZone(store.timezone)}
            open={dialog === 'new'}
            onOpenChange={(open) => {
              if (!open) setDialog(null);
            }}
            saleId={sale.id}
          />
        </>
      ) : null}
    </Card>
  );
}
